import { useEffect, useState, type MouseEvent } from 'react';
import { BellOff, CheckCheck, CircleAlert, ExternalLink, Settings } from 'lucide-react';
import { cn } from '../../lib/cn';
import { formatVor } from '../../lib/format';
import { Button, buttonVariants } from '../../components/ui/button';
import { Icon } from '../../components/ui/icon';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Sheet } from '../../components/ui/sheet';
import { Skeleton } from '../../components/ui/skeleton';
import { Callout } from '../../components/elbklar/callout';
import { EmptyState } from '../../components/elbklar/empty-state';
import { Pagination } from '../../components/elbklar/pagination';
import { useAlleGelesen, useGelesen, useListe, useNavigation, useZaehler } from './kontext';
import { APP_NAME, ziel } from './logik';
import type { Ansicht, Benachrichtigung } from './typen';

export function BenachrichtigungenSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [bereich, setBereich] = useState<Ansicht>('neu');
  const ungelesen = useZaehler().data?.unread_count ?? 0;
  const { kontoEinstellungen } = useNavigation();
  // Wer auf die Zahl am Eintrag tippt, will das Neue sehen, nicht den zuletzt gewählten Bereich.
  useEffect(() => {
    if (open) setBereich('neu');
  }, [open]);
  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="Benachrichtigungen">
      {/* Am Rechner feste Höhe: Beim Wechsel zwischen Neu und Alle springt der
          mittige Dialog sonst unter dem Mauszeiger weg. */}
      <div className="flex flex-col gap-4 md:h-[min(36rem,calc(100dvh-9rem))]">
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
          <SegmentedControl
            label="Bereich"
            value={bereich}
            onChange={(v) => setBereich(v as Ansicht)}
            items={[
              { value: 'neu', label: 'Neu', count: ungelesen > 0 ? ungelesen : undefined },
              { value: 'alle', label: 'Alle' },
            ]}
          />
          {/* Die Einstellungen liegen nur in Konto (Henri, 28.10.2026): eine
              Stelle für alle Apps, mit Rückweg hierher. */}
          <a href={kontoEinstellungen()} className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
            <Icon icon={Settings} size={16} />
            Einstellungen
            <span className="sr-only">in Konto</span>
            <Icon icon={ExternalLink} size={14} className="text-muted-foreground" />
          </a>
        </div>
        <div className="md:-mx-1 md:min-h-0 md:flex-1 md:overflow-y-auto md:px-1">
          <Liste key={bereich} ansicht={bereich} aktiv={open} />
        </div>
      </div>
    </Sheet>
  );
}

function Liste({ ansicht, aktiv }: { ansicht: Ansicht; aktiv: boolean }) {
  const [seite, setSeite] = useState(1);
  const liste = useListe(ansicht, seite, aktiv);
  const alleGelesen = useAlleGelesen();
  const daten = liste.data;
  // Schrumpft die Liste (gelesen markiert), nicht auf einer leeren Seite stehen bleiben.
  const seiten = daten?.pages ?? 1;
  useEffect(() => {
    if (seite > Math.max(1, seiten)) setSeite(Math.max(1, seiten));
  }, [seite, seiten]);

  if (liste.isPending) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-14" />
        ))}
      </div>
    );
  }
  if (liste.isError || !daten) {
    return (
      <Callout tone="danger" icon={CircleAlert} title="Benachrichtigungen ließen sich nicht laden" boxed>
        <Button size="sm" className="mt-2" onClick={() => liste.refetch()}>
          Erneut laden
        </Button>
      </Callout>
    );
  }
  if (daten.items.length === 0) {
    return (
      <EmptyState
        icon={BellOff}
        title={ansicht === 'neu' ? 'Keine neuen Benachrichtigungen' : 'Noch keine Benachrichtigungen'}
        description={ansicht === 'neu' ? 'Neue Benachrichtigungen erscheinen hier.' : 'Benachrichtigungen aus allen Apps erscheinen hier, sobald es welche gibt.'}
      />
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {daten.unread_count > 0 && (
        <Button size="sm" variant="ghost" icon={CheckCheck} className="self-end" loading={alleGelesen.isPending} onClick={() => alleGelesen.mutate()}>
          Alle als gelesen markieren
        </Button>
      )}
      <ul className="-mx-2 flex flex-col">
        {daten.items.map((b) => (
          <li key={b.id}>
            <Zeile eintrag={b} />
          </li>
        ))}
      </ul>
      {daten.pages > 1 && <Pagination page={seite} pageCount={daten.pages} onPageChange={setSeite} />}
    </div>
  );
}

function Zeile({ eintrag }: { eintrag: Benachrichtigung }) {
  const { app, navigieren, schliessen } = useNavigation();
  const gelesen = useGelesen();
  const wohin = ziel(eintrag, app, window.location.origin);
  const fremd = eintrag.app && eintrag.app !== app ? APP_NAME[eintrag.app] : null;

  const markieren = () => {
    if (!eintrag.is_read) gelesen.mutate(eintrag.id);
  };

  const oeffnen = async (e: MouseEvent) => {
    // Mit Strg/Cmd oder mittlerer Taste: neuer Tab wie bei jedem Link.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
      markieren();
      return;
    }
    e.preventDefault();
    if (!wohin) {
      markieren();
      return;
    }
    if (wohin.art === 'intern') {
      markieren();
      schliessen();
      navigieren(wohin.pfad);
      return;
    }
    // Andere App: erst gelesen speichern, dann wechseln, sonst bricht der Seitenwechsel den Aufruf ab.
    if (!eintrag.is_read) await gelesen.mutateAsync(eintrag.id).catch(() => undefined);
    window.location.assign(wohin.href);
  };

  const inhalt = (
    <>
      <span
        aria-hidden
        className={cn('mt-1.5 size-2 shrink-0 rounded-full', eintrag.is_read ? 'bg-transparent' : 'bg-selection')}
      />
      <span className="min-w-0 flex-1">
        <span className={cn('block line-clamp-2', eintrag.is_read ? 't-body' : 't-body-strong')}>{eintrag.title}</span>
        {eintrag.message && <span className="mt-0.5 block line-clamp-2 t-small text-muted-foreground">{eintrag.message}</span>}
        <span className="mt-1 block t-small text-muted-foreground">
          {formatVor(eintrag.created_at)}
          {fremd && ` · ${fremd}`}
          {!eintrag.is_read && <span className="sr-only"> · ungelesen</span>}
        </span>
      </span>
      {wohin?.art === 'extern' && <Icon icon={ExternalLink} size={16} className="mt-1 text-muted-foreground" />}
    </>
  );

  const klasse =
    'flex w-full cursor-pointer items-start gap-3 rounded-md px-2 py-2.5 text-left transition-colors duration-150 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring touch:min-h-[52px]';

  if (wohin) {
    const href = wohin.art === 'intern' ? wohin.pfad : wohin.href;
    return (
      <a href={href} onClick={oeffnen} className={klasse}>
        {inhalt}
      </a>
    );
  }
  // Ohne Ziel ist eine gelesene Zeile nur Text; eine ungelesene markiert beim Antippen.
  if (eintrag.is_read) {
    return <div className={cn(klasse, 'cursor-default hover:bg-transparent')}>{inhalt}</div>;
  }
  return (
    <button type="button" onClick={oeffnen} className={klasse}>
      {inhalt}
      <span className="sr-only">Als gelesen markieren</span>
    </button>
  );
}

